import React from 'react';
import CardComponent from '../components/Card';
import { Alert, Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import { loadCoursesData } from '../services/courseData';
import Seo from '../components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { COURSE_PATHS, getCoursePath } from '../coursePaths';

function CardPage() {
  const [courses, setCourses] = React.useState([]);
  const [searchParams] = useSearchParams();
  const selectedPath = searchParams.get('path');
  const pathDetails = COURSE_PATHS[selectedPath];
  const visibleCourses = pathDetails
    ? courses.filter((course) => getCoursePath(course) === selectedPath)
    : courses;

  React.useEffect(() => {
    let active = true;

    loadCoursesData()
      .then((data) => {
        if (active) {
          setCourses(data.courses || []);
        }
      })
      .catch(() => {
        if (active) {
          setCourses([]);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Seo
        title={pathDetails ? `${pathDetails.title} Labs` : 'Linux and DevOps Lab Courses'}
        description={pathDetails?.description || 'Browse practical Linux and DevOps lab courses with guided lesson content, terminal sessions, and built-in task checks.'}
        path={pathDetails ? `/lesson?path=${selectedPath}` : '/lesson'}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pathDetails ? `NixDojo ${pathDetails.title} Labs` : 'NixDojo Linux and DevOps Lab Courses',
          url: 'https://nixdojo.com/lesson',
          description: 'Practical Linux and DevOps lab courses with guided lessons and browser terminal practice.'
        }}
      />
      <Box sx={{ mb: 4, maxWidth: 720 }}>
        <Typography variant='h3' sx={{ mb: 1.5 }}>
          {pathDetails ? pathDetails.title : 'All lab courses'}
        </Typography>
        <Typography color='text.secondary'>
          {pathDetails?.description || 'Choose a course to enter the lesson flow, start a terminal session, and run the built-in checks.'}
        </Typography>
        <Stack direction='row' useFlexGap flexWrap='wrap' gap={1} sx={{ mt: 2.5 }} aria-label='Filter labs by learning path'>
          <Chip component={Link} clickable to='/lesson' label='All labs' color={!pathDetails ? 'primary' : 'default'} variant={!pathDetails ? 'filled' : 'outlined'} />
          {Object.entries(COURSE_PATHS).map(([path, details]) => (
            <Chip key={path} component={Link} clickable to={`/lesson?path=${path}`} label={details.title} color={selectedPath === path ? 'primary' : 'default'} variant={selectedPath === path ? 'filled' : 'outlined'} />
          ))}
        </Stack>
      </Box>
      {pathDetails && visibleCourses.length === 0 && (
        <Alert severity='info' sx={{ mb: 3 }} action={<Button component={Link} to='/lesson' color='inherit'>View all labs</Button>}>
          No labs are available in this learning path yet.
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(auto-fit, minmax(300px, 1fr))',
            sm: 'repeat(auto-fit, minmax(320px, 1fr))'
          },
          gap: 3,
          alignItems: 'stretch'
        }}
      >
        {visibleCourses.map((course) => (
          <CardComponent key={course.id} title={course.title} text={course.description} courseId={course.id} />
        ))}
      </Box>
    </Container>
  );
}

export default CardPage;
